import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { fmtNum, fmtUSD, fmtPct } from "@/lib/format";

const ACCENT = "#9E1B22";

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className="mt-1 font-mono text-lg font-bold tabular-nums text-[#171717]">{value}</div>
  </div>
);

export const BreakdownModal = ({ open, onClose, data }) => {
  if (!data) return null;
  const hasHistory = (data.history || []).some((p) => p.streams > 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="breakdown-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{data.name}</DialogTitle>
          <DialogDescription className="capitalize">
            Desglose {{ platform: "de plataforma", country: "por país", city: "por ciudad", track: "por canción", product: "por producto", store: "por tienda", transaction: "por tipo de transacción" }[data.kind] || "detallado"}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2" data-testid="breakdown-stats">
          <Stat label="Reproducciones" value={data.stream_count != null ? fmtNum(data.stream_count) : "—"} />
          <Stat label="Participación" value={data.pct != null ? fmtPct(data.pct) : "—"} />
          <Stat label="Ingresos" value={data.revenue_usd != null ? fmtUSD(data.revenue_usd) : "—"} />
        </div>

        {hasHistory && (
          <div className="mt-2">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Histórico</div>
            <div className="h-28 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.history} margin={{ top: 6, right: 4, left: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="bdGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={ACCENT} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" hide />
                  <Tooltip formatter={(v) => [fmtNum(v), "Streams"]} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Area type="monotone" dataKey="streams" stroke={ACCENT} strokeWidth={2} fill="url(#bdGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {!!(data.top_tracks || []).length && (
          <div className="mt-2">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Canciones con más streams aquí</div>
            <div className="space-y-1.5" data-testid="breakdown-tracks">
              {data.top_tracks.map((t, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="font-mono text-xs text-slate-400">#{i + 1}</span>
                    <span className="truncate font-medium text-slate-800">{t.title}</span>
                    {t.isrc && <span className="hidden font-mono text-[10px] text-slate-400 sm:inline">{t.isrc}</span>}
                  </span>
                  <span className="shrink-0 font-mono text-xs tabular-nums text-slate-600">
                    {fmtNum(t.streams)}{t.revenue_usd != null ? ` · ${fmtUSD(t.revenue_usd)}` : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.estimated && (
          <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400">
            <Sparkles className="h-3 w-3" /> Estimación proporcional · estructura lista para datos reales del backend
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};
